import { useEffect, useState } from "react";
import { createConsumer } from "@rails/actioncable";

type ConnectionState = "connected" | "connecting" | "disconnected";

let sharedConsumer: ReturnType<typeof createConsumer> | null = null;

function getConsumer() {
  if (sharedConsumer) return sharedConsumer;
  sharedConsumer = createConsumer(
    `${window.location.origin.replace(/^http/, "ws")}/cable`,
  );
  return sharedConsumer;
}

/**
 * Subscribes to the underlying ActionCable consumer's connection
 * lifecycle events. Returns "connected" / "connecting" / "disconnected"
 * which the UI can use to show a "Reconnecting…" banner.
 *
 * Apollo's own ActionCableLink uses a consumer it creates internally,
 * so we open a parallel consumer here just for monitoring. The
 * connection cost is one extra WebSocket which Cable already
 * multiplexes — negligible.
 */
export function useConnectionState(): ConnectionState {
  const [state, setState] = useState<ConnectionState>("connecting");

  useEffect(() => {
    const consumer = getConsumer();
    let cancelled = false;

    const sub = consumer.subscriptions.create(
      { channel: "ApplicationCable::Channel" },
      {
        connected() {
          if (!cancelled) setState("connected");
        },
        disconnected() {
          if (!cancelled) setState("disconnected");
        },
      },
    );

    // Poll the consumer's underlying WebSocket every second so we
    // catch transitions even when ActionCable doesn't fire its own
    // lifecycle callback (e.g., page suspended → resumed).
    const poll = setInterval(() => {
      if (cancelled) return;
      const ws =
        (consumer.connection as unknown as { webSocket?: WebSocket }).webSocket;
      if (!ws) return;
      if (ws.readyState === WebSocket.OPEN) setState("connected");
      else if (ws.readyState === WebSocket.CONNECTING) setState("connecting");
      else setState("disconnected");
    }, 1000);

    return () => {
      cancelled = true;
      clearInterval(poll);
      sub.unsubscribe();
    };
  }, []);

  return state;
}
